import createKindeClient from "@kinde-oss/kinde-auth-pkce-js";
import { displayLoggedInView, displayLoggedOutView, setUser, getUser } from '../ui/views';
import { updateStats } from '../ui/stats';
import { updateInventoryDisplay } from '../ui/inventory';
import { showUsernameModal, hideAllModals } from '../ui/modals';

let kinde;
let kindeReadyPromise = null;
let sessionRestorePromise = null;
// Empty string keeps API calls same-origin — Vercel proxies /api/* to the
// backend server (see vercel.json). Avoids Safari ITP blocking cross-site
// fetches with "Load failed".
const SERVER_URL = '';
const IDENTIFIED_FLAG = 'snordeluxe_identified';

// Playwright auth bypass. The e2e harness injects window.__SNORDELUXE_TEST_USER__
// before page scripts run; when present, we skip Kinde init entirely and
// hand back the fake user. Gated to localhost so an attacker on prod can't
// inject themselves into the UI by setting the global in devtools (they
// still couldn't talk to the backend without a token, but better to lock
// the door anyway).
function getTestUserOverride() {
    try {
        if (typeof window === 'undefined') return null;
        const host = window.location && window.location.hostname;
        if (host !== 'localhost' && host !== '127.0.0.1') return null;
        return window.__SNORDELUXE_TEST_USER__ || null;
    } catch (_) {
        return null;
    }
}

const markIdentified = () => {
    try { localStorage.setItem(IDENTIFIED_FLAG, '1'); } catch (_) {}
};

const isIdentified = () => {
    try { return localStorage.getItem(IDENTIFIED_FLAG) === '1'; } catch (_) { return false; }
};

const clearIdentified = () => {
    try { localStorage.removeItem(IDENTIFIED_FLAG); } catch (_) {}
};

const initializeKindeClient = () => {
    if (kindeReadyPromise) return kindeReadyPromise;
    kindeReadyPromise = (async () => {
        try {
            kinde = await createKindeClient({
                client_id: "699dbb0bd6354acabe46b158a94f43de",
                domain: "https://snordeluxe.kinde.com",
                redirect_uri: window.location.origin,
                // Persist refresh token in localStorage so the user doesn't have
                // to sign in on every page load. Without this the session is
                // cookie-only and is lost across browser reloads in production.
                is_dangerously_use_local_storage: true
            });
            console.log("Kinde client initialized.");
        } catch (error) {
            console.error("Failed to initialize Kinde client:", error);
            // Allow a retry on the next call rather than caching the failure.
            kindeReadyPromise = null;
        }
    })();
    return kindeReadyPromise;
};

const waitForKinde = async () => {
    if (!kinde) await initializeKindeClient();
    return kinde;
};

const checkUserSession = async () => {
    const testUser = getTestUserOverride();
    if (testUser) {
        console.log('[KindeClient] TEST USER override active — skipping Kinde.');
        // Re-attach cached stat overrides so a page reload restores any
        // upgrades the test session already made (same persistence path as
        // the real fetchUserData flow).
        const restored = applyStatOverridesToUser(testUser);
        applyDefeatedToUserSync(restored);
        setUser(restored);
        // Real-login flow goes through fetchUserData which paints
        // stats; the test-user bypass skipped that, leaving the lobby
        // showing ⭐️ 0 instead of the test user's mana. Paint stats
        // here so e2e selectors that read #manaStat reflect reality.
        try { updateStats(restored.mana || 0, restored.coins || 0, restored.gems || 0); } catch (_) {}
        markIdentified();
        return restored;
    }
    if (sessionRestorePromise) return sessionRestorePromise;
    sessionRestorePromise = (async () => {
        try {
            await waitForKinde();
            if (!kinde) return null;
            return await runSessionCheck();
        } catch (error) {
            console.error("Error restoring session:", error);
            handleSessionError(error);
            return null;
        }
    })();
    return sessionRestorePromise;
};

const runSessionCheck = async () => {
    try {
        const isAuthed = await kinde.isAuthenticated();
        if (!isAuthed) {
            console.log("No active Kinde session.");
            return null;
        }
        const kindeUser = await kinde.getUser();
        if (!kindeUser || !kindeUser.id) {
            console.log("Authenticated but no user profile available.");
            return null;
        }
        setUser(kindeUser);
        markIdentified();
        // Stash the verified email so the admin UI gate (isOwner) can
        // read it without re-asking Kinde. Server still enforces the
        // real check on every API call.
        if (typeof window !== 'undefined' && kindeUser.email) {
            window.__currentUserEmail__ = String(kindeUser.email).toLowerCase();
        }
        const merged = await fetchUserData(kindeUser.id);
        if (!merged) {
            // Server fetch failed; keep the Kinde user so the UI can still load
            // (player will see logged-in state with default stats).
            return kindeUser;
        }
        setUser(merged);
        return merged;
    } catch (error) {
        console.error("Error restoring session:", error);
        handleSessionError(error);
        return null;
    }
};

const handleLogin = async () => {
    console.log("Handling login");
    try {
        await waitForKinde();
        if (!kinde) {
            console.error("Kinde failed to initialize; cannot log in.");
            displayLoggedOutView();
            return;
        }
        // If a session restore is already in flight (started at page load),
        // wait for it. Otherwise the user may click Play before the local
        // refresh token has been validated, and we'd needlessly redirect
        // to Kinde even though they were about to be authenticated.
        if (sessionRestorePromise) {
            const restored = await sessionRestorePromise;
            if (restored) setUser(restored);
        }
        let user = getUser();
        if (!user || !user.id) {
            // Returning users (identified before on this device) get the login
            // flow — Kinde silently completes the redirect when their session
            // is still alive. First-time visitors get the registration flow.
            if (isIdentified()) {
                console.log("Returning user — attempting silent login...");
                await kinde.login();
            } else {
                console.log("No user session found, attempting registration...");
                await kinde.register();
            }
            return;
        }
        markIdentified();

        // If checkUserSession already fetched the profile, skip the duplicate
        // network round-trip and just show the logged-in view directly.
        if (user.username !== undefined || user.coins !== undefined) {
            displayLoggedInView();
            if (!user.username) {
                showUsernameModal();
            }
            return;
        }

        console.log('Getting token...');
        const token = await kinde.getToken();
        if (!token) {
            throw new Error("Failed to retrieve token.");
        }
        localStorage.setItem('accessToken', token);

        console.log('Fetching user data...');
        const updatedUser = await fetchUserData(user.id);
        if (!updatedUser) {
            throw new Error("Could not load user data");
        }
        setUser(updatedUser);
        displayLoggedInView();

        if (!updatedUser.username) {
            showUsernameModal();
        }
    } catch (error) {
        console.error("Error during registration/login or fetching user data:", error);
        displayLoggedOutView();
    }
};

const handleSessionError = (error) => {
    console.error("Handling session error:", error);
    if (error.response && error.response.status === 403) {
        console.error("Access denied.");
    } else if (error.response && error.response.status === 404) {
        console.error("Resource not found.");
    } else if (error.message.includes('Network Error')) {
        console.error("Network error.");
    } else {
        console.error("An unexpected error occurred:", error.message);
    }
};

const fetchUserData = async (userId) => {
    console.log(`Fetching user data from database for ID: ${userId}`);
    const token = await kinde.getToken();
    const url = `${SERVER_URL}/api/user/${userId}`;

    try {
        const response = await fetch(url, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${token}`,
                'Content-Type': 'application/json'
            }
        });

        if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
        }

        const updatedData = await response.json();
        console.log("Received user data:", updatedData);

        let user = getUser();
        user = { ...user, ...updatedData };
        // Re-attach client-cached stat overrides. The backend doesn't store
        // staminaStat/powerStat/weightStat so without this every reload
        // would wipe the player's upgrades.
        user = applyStatOverridesToUser(user);
        // Same story for the shop's defeat-to-unlock list.
        applyDefeatedToUserSync(user);
        setUser(user); // Set the user
        user.coins = isNaN(user.coins) ? 0 : user.coins;
        user.gems = isNaN(user.gems) ? 0 : user.gems;
        user.mana = isNaN(user.mana) ? 0 : user.mana;

        console.log(`Updating stats with: ${user.mana} ${user.coins} ${user.gems}`);
        updateStats(user.mana, user.coins, user.gems);
        updateInventoryDisplay();
        return user;
    } catch (error) {
        console.error("Error fetching or processing user data:", error);
        return null;
    }
};

// Read the localStorage-cached defeated-snordeluxes list and stamp it onto
// the user. Mirrors applyStatOverridesToUser; lives here to keep both
// session-restore paths (test-bypass + fetchUserData) symmetric.
function applyDefeatedToUserSync(user) {
    if (!user || !user.id || typeof localStorage === 'undefined') return user;
    try {
        const raw = localStorage.getItem('snordeluxe-defeated-' + user.id);
        const parsed = raw ? JSON.parse(raw) : [];
        user.defeatedSnordeluxes = Array.isArray(parsed) ? parsed.filter(s => typeof s === 'string') : [];
    } catch (_) {
        user.defeatedSnordeluxes = [];
    }
    return user;
}

// Per-inventory stat overrides (staminaStat / powerStat / weightStat) live
// only on the client — the snordeluxe-server backend strips unknown fields
// when it POSTs back its sanitized user record. Without a local cache,
// every page reload (or every saveUserData round-trip) would wipe stat
// upgrades. Keyed by user.id so multiple test users on the same browser
// keep separate stat sheets.
const STATS_LS_PREFIX = 'snordeluxe-stat-overrides-';

function loadStatOverrides(userId) {
    if (!userId || typeof localStorage === 'undefined') return {};
    try {
        const raw = localStorage.getItem(STATS_LS_PREFIX + userId);
        return raw ? JSON.parse(raw) : {};
    } catch (_) { return {}; }
}

function saveStatOverridesFor(user) {
    if (!user || !user.id || !Array.isArray(user.inventory)) return;
    if (typeof localStorage === 'undefined') return;
    const overrides = {};
    for (const item of user.inventory) {
        if (!item || !item.name) continue;
        // Only record entries with at least one non-zero stat — keeps the
        // payload small and avoids leaving stale entries for items the
        // player never upgraded.
        if (item.staminaStat || item.powerStat || item.weightStat) {
            overrides[item.name] = {
                staminaStat: item.staminaStat || 0,
                powerStat: item.powerStat || 0,
                weightStat: item.weightStat || 0,
            };
        }
    }
    try {
        localStorage.setItem(STATS_LS_PREFIX + user.id, JSON.stringify(overrides));
    } catch (_) {}
}

function applyStatOverridesToUser(user) {
    if (!user || !user.id || !Array.isArray(user.inventory)) return user;
    const overrides = loadStatOverrides(user.id);
    user.inventory = user.inventory.map(item => {
        if (!item || !item.name) return item;
        const o = overrides[item.name];
        if (!o) return item;
        return {
            ...item,
            staminaStat: o.staminaStat,
            powerStat: o.powerStat,
            weightStat: o.weightStat,
        };
    });
    return user;
}

const saveUserData = async () => {
    // Always persist stat overrides locally — even in test mode and even if
    // the backend call below fails — so a page reload restores the upgrades.
    saveStatOverridesFor(getUser());
    if (getTestUserOverride()) {
        // Test mode — never round-trip to the backend; the fake user is purely
        // in-memory for the test session.
        return { ok: true };
    }
    let user = getUser();
    console.log('Saving user data to database');
    console.log(user);
    try {
        await waitForKinde();
        if (!kinde) throw new Error('Auth not ready. Please refresh and try again.');
        const token = await kinde.getToken();
        if (!token) throw new Error('Your sign-in expired. Please refresh and sign in again.');
        let response;
        try {
            response = await fetch(`${SERVER_URL}/api/user`, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(user)
            });
        } catch (networkErr) {
            console.error('Network error saving user:', networkErr);
            if (typeof navigator !== 'undefined' && navigator.onLine === false) {
                throw new Error('You appear to be offline. Reconnect and try again.');
            }
            const detail = `${networkErr && networkErr.name || 'Error'}: ${networkErr && networkErr.message || 'unknown'}`;
            throw new Error(`Couldn't reach the server (${detail}). This is often caused by a browser privacy/ad-block extension blocking snordeluxe-server.vercel.app — try disabling it or use a different browser.`);
        }
        if (!response.ok) {
            if (response.status === 401 || response.status === 403) {
                throw new Error('Your sign-in expired. Please refresh and sign in again.');
            }
            const raw = await response.text();
            let detail = raw;
            try { detail = JSON.parse(raw).message || raw; } catch (_) {}
            console.error(`saveUserData failed (${response.status}):`, detail);
            throw new Error(`Server error (${response.status}). ${detail}`);
        }
        const updatedData = await response.json();
        // Merge the server's response back into the local user, but preserve
        // client-only inventory fields (staminaStat, powerStat, weightStat).
        // The backend strips unknown fields, so without this re-attach the
        // server's sanitized inventory array would wipe local stat upgrades
        // on every save (e.g. after feeding).
        const localInventory = Array.isArray(user.inventory) ? user.inventory : [];
        user = { ...user, ...updatedData };
        if (Array.isArray(user.inventory)) {
            user.inventory = user.inventory.map(serverItem => {
                const localItem = localInventory.find(li => li && li.name === serverItem.name);
                if (!localItem) return serverItem;
                return {
                    ...serverItem,
                    staminaStat: localItem.staminaStat,
                    powerStat: localItem.powerStat,
                    weightStat: localItem.weightStat,
                };
            });
        }
        // Re-persist after the server merge in case the inventory order /
        // contents shifted server-side (e.g. an item was added).
        saveStatOverridesFor(user);
        setUser(user);
        console.log("User data saved to server: ", user);
        return { ok: true };
    } catch (error) {
        console.error("Failed to save user data:", error);
        return { ok: false, error };
    }
};

const getKindeInstance = () => kinde;

export {
    initializeKindeClient,
    checkUserSession,
    handleLogin,
    fetchUserData,
    saveUserData,
    getKindeInstance,
    clearIdentified
};
